import Input from "@/components/Input/Input";
import { isNumber } from "@/utils/typeGuards";
import { memo, useCallback } from "react";
import type { FieldRenderInputProps } from "../../types";
import type { BaseFieldProps } from "./BaseField";
import { BaseField } from "./BaseField";

/**
 * 数値フィールドのプロパティ
 */
interface NumberFieldProps extends BaseFieldProps {
  /** 最小値 */
  min?: number;

  /** 最大値 */
  max?: number;

  /** 増減幅（ステップ値） */
  step?: number;

  /** プレースホルダー */
  placeholder?: string;
}

/**
 * 数値入力フィールドコンポーネント
 * 整数値または小数値の入力に特化したフィールド
 */
export const NumberField = memo(
  ({
    fieldId,
    field,
    layout,
    readOnly,
    disabled,
    className,
    min,
    max,
    step,
    "data-testid": testId,
  }: NumberFieldProps) => {
    // 入力レンダリング関数
    const renderInput = useCallback(
      (props: FieldRenderInputProps) => {
        // 数値を文字列として扱う（空の入力を許可するため）
        const stringValue =
          props.value !== undefined && props.value !== null
            ? String(props.value)
            : "";

        // 入力変更ハンドラー
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = e.target.value;

          // 空の場合はundefinedを設定
          if (newValue === "") {
            props.onChange(undefined);
            return;
          }

          // 数値に変換（+演算子を使用）
          const numValue = +newValue;

          // 有効な数値の場合のみ値を更新
          if (isNumber(numValue)) {
            props.onChange(numValue);
          }
        };

        return (
          <Input
            id={props.id}
            name={props.name}
            type="number"
            value={stringValue}
            onChange={handleChange}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            placeholder={props.placeholder}
            disabled={props.disabled}
            readOnly={props.readOnly}
            required={props.required}
            min={min}
            max={max}
            step={step}
            error={!!props.error}
            aria-invalid={!!props.error}
            aria-describedby={props.error ? `${props.id}-error` : undefined}
            width="100%"
            className="w-full"
            data-testid={props["data-testid"] || "number-field"}
          />
        );
      },
      [min, max, step],
    );

    // BaseFieldを使用して数値フィールドをレンダリング
    return (
      <BaseField
        fieldId={fieldId}
        field={field}
        layout={layout}
        readOnly={readOnly}
        disabled={disabled}
        className={className}
        data-testid={testId}
        renderInput={renderInput}
      />
    );
  },
);

NumberField.displayName = "NumberField";
